import { Link, useLocation, useNavigate } from 'react-router'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '#app/components/ui/navigation-menu.tsx'
import { cn } from '#app/utils/misc.tsx'
import {
	type Section,
	type SectionPage,
	SECTIONS,
	isSectionActive,
	isPageActive,
} from '#app/utils/site-sections.ts'

/**
 * Top navigation. For each section:
 * - 0 child pages → flat link to the section hub.
 * - 1+ child pages → mega-menu dropdown listing children with descriptions in
 *   a 2-column grid.
 *
 * The active state lights up:
 * - On the top item: whenever the user is anywhere inside its section.
 * - On a dropdown child: when the current pathname matches that page exactly.
 *
 * Hidden on mobile (mobile nav is a separate component).
 */
export function SiteNav({ className }: { className?: string }) {
	const { pathname } = useLocation()

	return (
		<NavigationMenu
			className={cn('hidden lg:flex', className)}
			viewport={false}
		>
			<NavigationMenuList>
				{SECTIONS.map((section) => {
					const active = isSectionActive(pathname, section)

					if (section.pages.length === 0) {
						return (
							<NavigationMenuItem key={section.to}>
								<NavigationMenuLink
									asChild
									active={active}
									className={navigationMenuTriggerStyle()}
								>
									<Link to={section.to}>{section.label}</Link>
								</NavigationMenuLink>
							</NavigationMenuItem>
						)
					}

					return (
						<MegaMenuItem
							key={section.to}
							section={section}
							active={active}
							pathname={pathname}
						/>
					)
				})}
			</NavigationMenuList>
		</NavigationMenu>
	)
}

function MegaMenuItem({
	section,
	active,
	pathname,
}: {
	section: Section
	active: boolean
	pathname: string
}) {
	const navigate = useNavigate()
	return (
		<NavigationMenuItem>
			<NavigationMenuTrigger
				// Hover still opens the dropdown (Radix default). Clicking the
				// trigger label navigates to the section hub instead of just
				// toggling the panel — matches the mobile menu, where tapping
				// the section header navigates.
				onClick={() => navigate(section.to)}
				data-active={active || undefined}
				className={
					active ? 'text-primary data-[state=open]:text-primary' : undefined
				}
			>
				{section.label}
			</NavigationMenuTrigger>
			{/* z-50 so the panel overlays page content (sections lower in the page
			    can create new stacking contexts that would otherwise sit on top). */}
			<NavigationMenuContent className="z-50">
				<ul className="grid w-96 grid-cols-2 gap-2 p-2 xl:w-125">
					{section.pages.map((page) => (
						<ListItem
							key={page.to}
							page={page}
							active={isPageActive(pathname, page)}
						/>
					))}
				</ul>
			</NavigationMenuContent>
		</NavigationMenuItem>
	)
}

function ListItem({ page, active }: { page: SectionPage; active: boolean }) {
	return (
		<li>
			<NavigationMenuLink asChild active={active}>
				<Link
					to={page.to}
					className="flex! flex-col! items-start! gap-1 p-3 leading-none no-underline select-none"
				>
					<span className="text-sm font-semibold leading-none">
						{page.label}
					</span>
					{page.description ? (
						<span className="text-muted-foreground line-clamp-2 text-xs leading-snug">
							{page.description}
						</span>
					) : null}
				</Link>
			</NavigationMenuLink>
		</li>
	)
}
